import {
  Box,
  Flex,
  Text,
  Tooltip,
  useColorModeValue
} from '@chakra-ui/react';
import React from 'react';
import { CLIENT_CONFIG } from '../../core/client/buildConfig';

interface SidebarItemProps {
  icon?: React.ReactElement | null;
  label: string;
  onClick?: () => void;
  isSelected?: boolean;
  showLabel?: boolean;
  badge?: string | number;
  compact?: boolean;
  // When true, the label is treated as sensitive: rendered with
  // `data-dd-privacy="mask"` so Datadog Session Replay redacts it, and the
  // hover Tooltip is suppressed (Chakra renders it in a portal that does not
  // inherit the privacy attribute).
  privacyMask?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  icon,
  label,
  onClick,
  isSelected = false,
  showLabel = true,
  badge,
  compact = false,
  privacyMask = false,
}) => {
  // Light mode tints with the brand; dark mode steps one shade up from the
  // gray.800 sidebar so the highlight stays a soft grey under white text.
  const selectedBgColor = useColorModeValue('primaryScheme.50', 'gray.700');
  const hoverBgColor = useColorModeValue('primaryScheme.50', 'gray.700');
  const selectedIndicatorColor = CLIENT_CONFIG.theme.colors.accent;
  const selectedTextColor = CLIENT_CONFIG.theme.colors.accent;
  const badgeBgColor = CLIENT_CONFIG.theme.colors.secondary;

  const content = (
    <Flex
      py={compact ? 1 : 2}
      px={2}
      alignItems="center"
      borderRadius="md"
      cursor="pointer"
      onClick={onClick}
      transition="all 0.2s"
      justify={showLabel ? 'flex-start' : 'center'}
      position="relative"
      bg={isSelected ? selectedBgColor : 'transparent'}
      _hover={{
        bg: hoverBgColor,
      }}
      _before={{
        content: '""',
        position: 'absolute',
        top: '0',
        left: '0',
        bottom: '0',
        width: '3px',
        borderRadius: '0 4px 4px 0',
        bg: isSelected ? selectedIndicatorColor : 'transparent',
      }}
    >
      {icon && (
        <Box
          w="24px"
          h="24px"
          mr={showLabel ? (compact ? 1 : 2) : 0}
          fontSize="sm"
          opacity={0.8}
          color={isSelected ? selectedTextColor : 'inherit'}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          {icon}
        </Box>
      )}

      {showLabel && (
        <Text
          flex="1"
          fontSize="sm"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          fontWeight={isSelected ? 'medium' : 'normal'}
          color={isSelected ? selectedTextColor : 'inherit'}
          data-dd-privacy={privacyMask ? 'mask' : undefined}
        >
          {label}
        </Text>
      )}

      {badge && showLabel && (
        <Box
          fontSize="xs"
          px={2}
          py={0.5}
          borderRadius="full"
          bg={badgeBgColor}
          color="white"
          ml={2}
          lineHeight="normal"
        >
          {badge}
        </Box>
      )}
    </Flex>
  );

  if (privacyMask) {
    return content;
  }

  return (
    <Tooltip label={label} placement="top" openDelay={500}>
      {content}
    </Tooltip>
  );
};

export default SidebarItem;
